import { css, nothing } from 'lit'
import HHQuizElement from '../element.js'
import { html, unsafeStatic } from 'lit/static-html.js'
import router from '../router/store.js'

/**
 * Question view
 * @extends
 */

class HHQuizViewQuestion extends HHQuizElement {

  /**
   * Adds 'Enter' key listener
   */
  connectedCallback() {
    super.connectedCallback()
    window.addEventListener('keydown', this.handleKeydown, false)
  }

  /**
   * Removes 'Enter' key listener
   */
  disconnectedCallback() {
    window.removeEventListener('keydown', this.handleKeydown, false)
    super.disconnectedCallback()
  }

  /**
   * Current quiz question based on router handle param
   * @returns {Object}
   */
  get question() {
    return this.store.getQuestion(this.router.current.params.handle)
  }

  /**
   * Navigates to the next route
   * @param {Number} timeout
   * @returns {void}
   */
  nextRoute(timeout = 0) {
    clearTimeout(this.navigationTimeout)
    this.navigationTimeout = setTimeout(
      () => this.router.next(),
      timeout
    )
  }

  /**
   *  Handles question click
   *  @param {*} ev 
   *  @returns  {void}
   */
  handleOptionClick = (ev) => {
    if (this.question.answer_format === 'multiple-options') return
    this.nextRoute(ev.detail.wasPreviouslyCompleted ? (ev.detail.hasChanged ? 80 : 0) : 300)
  }

  /**
   * Handles 'Enter' keydown
   * @param {*} ev
   * @returns {void}
   */
  handleKeydown = (ev) => {
    if (ev.key !== 'Enter' || !this.question.isComplete) return
    this.nextRoute()
  }

  /**
   * Renders the current route
   * @returns {TemplateResult}
   */
  render() {
    return !!this.question
      ? html`
          <div class="hh-quiz-view-question">
            <div>
              <h1 class="h2 focus-first focus-none" tabindex="0">${this.question.question}</h1>
              ${this.question.description
                ? html`<${unsafeStatic(
                    this.question.description_modal ? `button` : 'p'
                  )}
                  @click="${() =>
                  this.question.description_modal
                    ? this.store.openModal(
                        'info',
                        this.question.description_modal
                      )
                  : null}"
                  @keydown="${(ev) => this.question.description_modal ? ev.stopPropagation() : null}"
                    class="body hh-quiz-view-question__description"
                  >
                    ${
                      !!this.question.description_modal
                        ? unsafeStatic(HH_QUIZ['icons'].info)
                        : nothing
                    }
                    <span>${this.question.description}</span>
                  </${unsafeStatic(
                    this.question.description_modal ? 'button' : 'p'
                  )}>`
                : ''}
            </div>
            <div>
              <hh-quiz-question-input
                question="${JSON.stringify(this.question)}"
                @click-option="${this.handleOptionClick}"
              />
            </div>
            <hh-quiz-router-link
              component="hh-button"
              .disabled="${!this.question.isComplete}"
              to="${this.router.nextPath}"
              class="${this.question.answer_format === 'multiple-options' ? '' : 'visually-hidden'}"
              >${HH_QUIZ.t.actions.continue}</hh-quiz-router-link
            >
          </div>
          ${this.question.learn_more_modal
            ? html`<button
                class="hh-quiz-view-question__learn-more body"
                @keydown="${(ev) => ev.stopPropagation()}"
                @click="${() =>
                  this.store.openModal('info', this.question.learn_more_modal)}"
              >
                ${unsafeStatic(HH_QUIZ['icons'].info)}
                ${this.question.learn_more ??
                this.question.learn_more_modal.title ??
                HH_QUIZ.t.actions.learn_more}
              </button>`
            : nothing}
        `
      : html`<hh-quiz-not-found></hh-quiz-not-found>`
  }

  static styles = [
    HHQuizElement.styles,
    css`
      .hh-quiz-view-question {
        max-width: max(68vw, 985px);
        padding: 60px 30px;
        margin: 0 auto;
        display: grid;
        gap: 40px;
        text-align: center;
      }

      @media screen and (max-width: ${HHQuizElement.mq.sm}px) {
        .hh-quiz-view-question {
          padding: 16px 20px 60px;
        }
      }

      .hh-quiz-view-question__description {
        color: var(--hh-color-ancient-ore);
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        padding: 0;
        margin: 20px auto 0;
      }

      button.hh-quiz-view-question__description {
        cursor: pointer;
      }

      .hh-quiz-view-question__description svg {
        margin-right: 10px;
      }

      hh-quiz-router-link {
        justify-self: center;
      }

      .hh-quiz-view-question__learn-more {
        position: fixed;
        z-index: calc(var(--hh-z-highest) * 5);
        left: 10px;
        bottom: 10px;
        border: none;
        padding: 0;
        margin: 0 auto;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        background: var(--hh-color-star-white);
        color: var(--hh-color-aged-black);
        transition: background 0.2s ease, color 0.2s ease;
        border-radius: 4px;
      }

      .hh-quiz-view-question__learn-more:hover {
        background: var(--hh-color-aged-black);
        color: var(--hh-color-star-white);
      }

      .hh-quiz-view-question__learn-more svg {
        margin-right: 10px;
      }

      @media screen and (max-width: ${HHQuizElement.mq.sm}px) {
        .hh-quiz-view-question__learn-more {
          position: relative;
          left: 0;
          margin-bottom: 20px;
          margin-top:-10px;
        }
      }
    `,
  ]
}

customElements.define('hh-quiz-view-question', HHQuizViewQuestion)
